/**
 * Утиліти реквізитів банківського рахунку для рахунку на оплату.
 */

export const normalizeIban = (iban) => String(iban || '').replace(/\s/g, '').toUpperCase();

export const normalizeInvoiceRequisites = ({
  recipientName,
  edrpou,
  iban,
  bankName,
} = {}) => ({
  recipientName: String(recipientName || '').trim(),
  edrpou: String(edrpou || '').trim(),
  iban: normalizeIban(iban),
  bankName: String(bankName || '').trim(),
});

export const resolveBankType = (bankTypeOrAccount) => {
  const raw = bankTypeOrAccount && typeof bankTypeOrAccount === 'object' && 'bankType' in bankTypeOrAccount
    ? bankTypeOrAccount.bankType
    : bankTypeOrAccount;
  if (!raw) return 'MONOBANK';
  let value = null;
  if (typeof raw === 'object' && raw !== null) {
    value = raw.databaseValue || raw.name || String(raw);
  } else {
    value = String(raw);
  }
  return String(value).toUpperCase() === 'PRIVATBANK' ? 'PRIVATBANK' : 'MONOBANK';
};

export const bankTypeLabelUa = (bankTypeOrAccount) => (
  resolveBankType(bankTypeOrAccount) === 'PRIVATBANK' ? 'ПриватБанк' : 'Монобанк'
);

/** Чи заповнені 4 поля реквізитів (без перевірки isActive). */
export const hasBankAccountInvoiceRequisites = (account) => {
  if (!account) return false;
  const req = normalizeInvoiceRequisites(account);
  return Boolean(req.recipientName && req.edrpou && req.iban && req.bankName);
};

/** Активний рахунок з повними реквізитами — можна обирати в модалці рахунку. */
export const isBankAccountReadyForInvoice = (account) => (
  Boolean(account)
  && account.isActive !== false
  && hasBankAccountInvoiceRequisites(account)
);

/** Валідація форми реквізитів. Повертає текст помилки або null.
 * Порожні поля дозволені (чернетка); формат перевіряється лише для заповнених.
 */
export const validateInvoiceRequisitesForm = (fields) => {
  const { recipientName, edrpou, iban, bankName } = normalizeInvoiceRequisites(fields);

  if (!recipientName && !edrpou && !iban && !bankName) {
    return 'Вкажіть хоча б одне поле реквізитів';
  }
  if (edrpou && !/^\d{8}(\d{2})?$/.test(edrpou)) {
    return 'ЄДРПОУ має містити 8 або 10 цифр';
  }
  // UA + 27 символів (цифри; допускаємо також літери BBAN на випадок нестандартного вводу)
  if (iban && !/^UA[0-9A-Z]{27}$/.test(iban)) {
    return 'IBAN має бути у форматі UA + 27 символів (без пробілів)';
  }
  return null;
};

/** Supplier для Excel лише з реквізитів рахунку (без підміни чужими дефолтами). */
export const supplierFromBankAccount = (account) => {
  if (!hasBankAccountInvoiceRequisites(account)) {
    throw new Error('У обраного банківського рахунку не заповнені реквізити для рахунку на оплату');
  }
  const req = normalizeInvoiceRequisites(account);
  return {
    name: req.recipientName,
    edrpou: req.edrpou,
    iban: req.iban,
    bank: req.bankName,
  };
};

export const resolveInvoiceSupplier = ({ bankAccount, supplier } = {}) => {
  if (supplier) return supplier;
  if (bankAccount) return supplierFromBankAccount(bankAccount);
  throw new Error('Оберіть банківський рахунок для рахунку на оплату');
};

/** Текст помилки з відповіді bankAccountsApi. */
export const getBankAccountsApiError = (err, fallback = 'Сталася помилка') => {
  const res = err?.response;
  return res?.data?.data || res?.data?.errorMessage || fallback;
};

export const formatOrdersCountLabel = (count) => {
  const n = Number(count) || 0;
  const mod10 = n % 10;
  const mod100 = n % 100;
  // 1, 21… — «замовлення»; 2–4, 22–24… — «замовлення»; інакше — «замовлень»
  const word = (mod100 >= 11 && mod100 <= 14) || mod10 === 0 || mod10 >= 5
    ? 'замовлень'
    : 'замовлення';
  return `${n} ${word}`;
};
